import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Categoria, CreateCategoriaInput } from '../types/categoria.types';

interface CategoriaFormProps {
  open: boolean;
  categoria?: Categoria | null;
  loading?: boolean;
  error?: string | null;
  onSave: (data: CreateCategoriaInput) => Promise<void>;
  onClose: () => void;
}

export function CategoriaForm({
  open,
  categoria,
  loading = false,
  error,
  onSave,
  onClose,
}: CategoriaFormProps) {
  const [formData, setFormData] = useState<CreateCategoriaInput>({
    nombre: '',
    descripcion: '',
    icono: '📦',
    color: '#1976d2',
    imagen: '',
    orden: 0,
  });
  const [activo, setActivo] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [colorInput, setColorInput] = useState('#1976d2');
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (categoria) {
      setFormData({
        nombre: categoria.nombre,
        descripcion: categoria.descripcion || '',
        icono: categoria.icono,
        color: categoria.color || '#1976d2',
        imagen: categoria.imagen || '',
        orden: categoria.orden,
      });
      setActivo(categoria.activo);
      setColorInput(categoria.color || '#1976d2');
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        icono: '📦',
        color: '#1976d2',
        imagen: '',
        orden: 0,
      });
      setActivo(true);
      setColorInput('#1976d2');
    }
    setValidationErrors({});
  }, [categoria, open]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'Nombre es requerido';
    }

    if (!formData.icono.trim()) {
      errors.icono = 'Icono es requerido';
    } else if (!/\p{Emoji}/u.test(formData.icono)) {
      errors.icono = 'Debe ser un emoji válido';
    }

    if (formData.color && !/^#[0-9A-Fa-f]{6}$/.test(formData.color)) {
      errors.color = 'Color debe ser hexadecimal válido (ej: #1976d2)';
    }

    if (typeof formData.orden === 'number' && formData.orden < 0) {
      errors.orden = 'Orden no puede ser negativa';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field: keyof CreateCategoriaInput, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleColorChange = (value: string) => {
    setColorInput(value);
    handleChange('color', value);
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setSubmitError(null);
    try {
      const submitData = {
        ...formData,
        activo,
      } as any;
      await onSave(submitData);
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al guardar la categoría';
      setSubmitError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const commonEmojis = ['📦', '👕', '👖', '👗', '👔', '🎒', '👠', '👞', '🧤', '🧣', '🎩', '👒', '💼', '👜', '🎁'];

  const isEditing = !!categoria;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Categoría' : 'Nueva Categoría'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {(error || submitError) && <Alert severity="error" sx={{ mb: 2 }}>{error || submitError}</Alert>}

        <Stack spacing={2}>
          {/* Nombre */}
          <TextField
            label="Nombre"
            value={formData.nombre}
            onChange={(e) => handleChange('nombre', e.target.value)}
            fullWidth
            error={!!validationErrors.nombre}
            helperText={validationErrors.nombre}
            disabled={submitting}
            required
          />

          {/* Descripción */}
          <TextField
            label="Descripción"
            value={formData.descripcion}
            onChange={(e) => handleChange('descripcion', e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={submitting}
          />

          {/* Icono */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Icono {formData.icono && <span style={{ fontSize: '1.5rem', marginLeft: '0.5rem' }}>{formData.icono}</span>}
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(40px, 1fr))',
                gap: 1,
                mb: 1,
              }}
            >
              {commonEmojis.map((emoji) => (
                <Button
                  key={emoji}
                  onClick={() => handleChange('icono', emoji)}
                  variant={formData.icono === emoji ? 'contained' : 'outlined'}
                  sx={{
                    fontSize: '1.5rem',
                    p: 0,
                    minWidth: '40px',
                    height: '40px',
                  }}
                  disabled={submitting}
                >
                  {emoji}
                </Button>
              ))}
            </Box>
            <TextField
              label="O ingresa un emoji"
              value={formData.icono}
              onChange={(e) => handleChange('icono', e.target.value.slice(0, 2))}
              fullWidth
              size="small"
              error={!!validationErrors.icono}
              helperText={validationErrors.icono}
              disabled={submitting}
            />
          </Box>

          {/* Color */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Color
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  backgroundColor: colorInput,
                  border: '1px solid #ddd',
                  borderRadius: 1,
                  cursor: 'pointer',
                }}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'color';
                  input.value = colorInput;
                  input.onchange = (e: any) => {
                    handleColorChange(e.target.value);
                  };
                  input.click();
                }}
              />
              <TextField
                label="Código Hexadecimal"
                value={colorInput}
                onChange={(e) => handleColorChange(e.target.value)}
                size="small"
                error={!!validationErrors.color}
                helperText={validationErrors.color || 'ej: #1976d2'}
                disabled={submitting}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>

          {/* Imagen URL */}
          <TextField
            label="URL de Imagen"
            value={formData.imagen}
            onChange={(e) => handleChange('imagen', e.target.value)}
            fullWidth
            type="url"
            placeholder="https://example.com/image.jpg"
            disabled={submitting}
          />

          {/* Orden */}
          <TextField
            label="Orden"
            value={formData.orden}
            onChange={(e) => handleChange('orden', parseInt(e.target.value) || 0)}
            fullWidth
            type="number"
            inputProps={{ min: 0 }}
            error={!!validationErrors.orden}
            helperText={validationErrors.orden}
            disabled={submitting}
          />

          {/* Activo */}
          {isEditing && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={activo}
                  onChange={(e) => setActivo(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Activo"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loading}
        >
          {submitting && <CircularProgress size={20} sx={{ mr: 1 }} />}
          {isEditing ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
